import { Alert, Box } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDeviceAlerts } from '@/api/customSensorAlerts/useDeviceAlerts';
import { useDeviceHasSensor } from '@/api/device';
import { SalinityDatum, useSalinityData } from '@/api/historicalData';
import { usePreferredTimeZone } from '@/api/userAccount';
import { salinityIcon } from '@/assets';
import { LineArea, LineAreaChart, LineDataSlice, drawLineAreaChart } from '@/charts';
import { colorsets, flattenSensorPositions, linesAcrossSensors } from '@/charts/common';
import { SensorIcon } from '@/components/data/SensorIcon';
import { ChartSensorSelector } from '@/components/spotter/historicalDataTab/ChartLineSelector';
import {
    ChartDataDetails,
    ChartDataSelectors,
    ChartWithInfo,
} from '@/components/spotter/historicalDataTab/ChartWithInfo';
import { DataDetailsByTimestamp } from '@/components/spotter/historicalDataTab/datumDetail/DataDetailPanel';
import {
    DataLineExtent,
    DataLineMean,
    LineChartDataSummary,
} from '@/components/spotter/historicalDataTab/datumDetail/LineChartDataSummary';
import { SalinityDatumDetail } from '@/components/spotter/historicalDataTab/datumDetail/SalinityDatumDetail';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { useSpotterContext } from '@/context/SpotterContext';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useChartContainer } from '@/hooks/useChartContainer';
import { useElementSize } from '@/hooks/useElementSize';
import { TrackChartViewWhenRendered } from '@/hooks/useIsViewedTracking';
import { useGlobalStore } from '@/store';
import { alertLineParamsForSensorChart, composeAlertLines } from '@/utils';

export const SALINITY_LINE_CONFIG_BASE = {
    valueKey: 'salinity',
    unitKey: 'units',
    unitTypeKey: 'unitType',
    yAreaKeyName: 'salinityStdDev',
    yAreaFn: (datum: SalinityDatum) => datum.salinityStdDev * 2,
    yAreaLabel: 'Std. deviation',
    label: 'Salinity',
    // placeholders, will be replaced per sensor
    id: 'salinity',
    color: colorsets.gray,
} satisfies LineConfig;

export type SalinityChartDatum = SalinityDatum & {
    sensorPosition: string;
};

type LineConfig = LineArea<SalinityChartDatum>;

export const SalinityLineChartSection = () => {
    const chartTitle = 'Salinity';
    const { spotterId } = useSpotterContext();
    const { hasSensor } = useDeviceHasSensor({ spotterId, type: 'salinity' });
    const { dateRange } = useDateRangeContext();
    const timeZone = usePreferredTimeZone();
    const isAlertLinesOn = useGlobalStore((state) => state.isAlertLinesOn);
    const isScaleToFitAlertLinesOn = useGlobalStore((state) => state.isScaleToFitAlertLinesOn);

    const { selectedTimestamp, setSelectedTimestamp } = useTimelineNavigationContext();

    const selectedTimestampRef = useRef(selectedTimestamp);

    selectedTimestampRef.current = selectedTimestamp;
    const [selectedData, setSelectedData] = useState<LineDataSlice<SalinityChartDatum, LineConfig> | null>(null);
    const [activeSensorPositions, setActiveSensorPositions] = useState<string[]>([]);
    const chartRef = useRef<LineAreaChart<SalinityChartDatum, LineConfig> | null>(null);
    const {
        data: sensorData,
        isPending,
        isFetching,
    } = useSalinityData({
        spotterId,
        dateRange,
    });
    const { setContainer, containerRef, containerEl } = useChartContainer();
    const { width, height } = useElementSize(containerRef);
    const { data: deviceAlerts } = useDeviceAlerts({
        config: { enabled: isAlertLinesOn },
    });

    const drawChart = useCallback(drawLineAreaChart, []);

    const { sensorPositions, dataset } = useMemo(() => flattenSensorPositions(sensorData), [sensorData]);

    const alertLines = useMemo(
        () =>
            composeAlertLines({
                deviceAlerts,
                triggerTypes: ['smartmooring_threshold_salinity'],
                ...alertLineParamsForSensorChart(sensorPositions, activeSensorPositions),
            }),
        [deviceAlerts, sensorPositions, activeSensorPositions]
    );

    const lines = useMemo(() => linesAcrossSensors([SALINITY_LINE_CONFIG_BASE], sensorPositions), [sensorPositions]);

    const activeLines = useMemo(
        () => lines.filter((l) => !activeSensorPositions.length || activeSensorPositions.includes(l.sensorPosition)),
        [activeSensorPositions, lines]
    );

    useEffect(() => {
        if (!containerEl || isPending) {
            return;
        }

        if (!dataset?.length) {
            return;
        }

        const newChart = drawChart<SalinityChartDatum, LineConfig>({
            dataset,
            lines: activeLines,
            xAxisKey: 'timestamp',
            yAxisLabel: 'Salinity',
            containerId: 'salinity-sm-chart',
            showAlertLines: isAlertLinesOn,
            scaleChartToFitAllAlerts: isScaleToFitAlertLinesOn,
            alertLines,
            chartWidth: width,
            chartHeight: height,
            dateRange,
            timeZone,
            onSelectData: setSelectedData,
            onSelectTimestamp: setSelectedTimestamp,
        });
        chartRef.current = newChart;
        newChart.setSelectedTimestamp?.(selectedTimestampRef.current ? new Date(selectedTimestampRef.current) : null);

        return () => {
            newChart.destroy();
            if (chartRef.current === newChart) {
                chartRef.current = null;
            }
        };
    }, [
        activeLines,
        containerEl,
        dataset,
        drawChart,
        height,
        isAlertLinesOn,
        alertLines,
        isPending,
        isScaleToFitAlertLinesOn,
        width,
        dateRange,
        timeZone,
        setSelectedTimestamp,
    ]);

    useEffect(() => {
        chartRef.current?.setSelectedTimestamp?.(selectedTimestamp ? new Date(selectedTimestamp) : null);
    }, [selectedTimestamp]);

    const hasData = Object.values(sensorData ?? {}).some((data) => data?.length > 0);

    if (!hasSensor && !hasData) {
        // no data in this window and no evidence that we're supposed to have the sensor; disappear
        return false;
    }

    return (
        <CollapsibleSection
            isFetching={isFetching}
            isPending={isPending}
            title={chartTitle}
            icon={<SensorIcon src={salinityIcon} />}
        >
            {hasData && dataset ? (
                <>
                    <TrackChartViewWhenRendered
                        containerEl={containerEl}
                        chartTitle={chartTitle}
                        spotterId={spotterId}
                    />
                    <ChartWithInfo chart={<Box ref={setContainer} id="salinity-sm-chart" width={1} height={1} />}>
                        <ChartDataSelectors>
                            <ChartSensorSelector
                                sensorPositions={sensorPositions}
                                onChange={setActiveSensorPositions}
                            />
                        </ChartDataSelectors>
                        <ChartDataDetails>
                            {!selectedData && (
                                <LineChartDataSummary
                                    dateRange={dateRange}
                                    lines={activeLines}
                                    dataset={dataset}
                                    renderLineSummary={() => (
                                        <>
                                            <DataLineMean />
                                            <DataLineExtent />
                                        </>
                                    )}
                                />
                            )}
                            <DataDetailsByTimestamp
                                selectedData={selectedData}
                                datumRenderer={({ lines, datum }) =>
                                    lines.map((line) => (
                                        <SalinityDatumDetail
                                            key={line.id}
                                            datum={datum}
                                            dataset={dataset}
                                            line={line}
                                        />
                                    ))
                                }
                            />
                        </ChartDataDetails>
                    </ChartWithInfo>
                </>
            ) : (
                <Alert severity="info">No salinity data for selected date range</Alert>
            )}
        </CollapsibleSection>
    );
};
